import React, { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  useRecordContext,
  Filter,
  TextInput,
  DateInput,
  SelectInput,
  useNotify,
} from "react-admin";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import PledgeStatusDialog from "./PledgeStatusDialog";

/* =========================
   FILTERS
========================= */
const PledgeFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Donor Name" source="userName" alwaysOn />

    <SelectInput
      label="Status"
      source="status"
      choices={[
        { id: "Pending", name: "Pending" },
        { id: "Approve", name: "Approve" },
        { id: "Reject", name: "Reject" },
      ]}
    />

    <DateInput label="From Date" source="fromDate" />
    <DateInput label="To Date" source="toDate" />
  </Filter>
);

/* =========================
   DELETE BUTTON
========================= */
const DeletePledgeButton = () => {
  const record = useRecordContext();
  const notify = useNotify();


  const handleDelete = async () => {
    const confirm = window.confirm("Are you sure you want to delete this pledge?");
    if (!confirm) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/pledges/${record.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Delete failed");
      }

      alert("Pledge deleted successfully");
      window.location.reload();
    } catch (err) {
      console.error(err);
      notify("Error deleting pledge", { type: "error" });
    }
  };

  return (
    <IconButton size="small" onClick={handleDelete}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

/* =========================
   STATUS FIELD + EDIT ICON
========================= */
const StatusWithEditIcon = ({ label }) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);

  if (!record) return null;

  const isApproved = record.status === "Approve";

  const getOptions = () => {
    switch (record.status) {
      case "Pending":
        return ["Approve", "Reject"];
      case "Reject":
        return ["Approve"];
      default:
        return [];
    }
  };

  return (
    <div>
      {label && (
        <div
          style={{
            fontWeight: "bold",
            marginBottom: 4,
            fontSize: "0.875rem",
            color: "rgba(0, 0, 0, 0.54)",
          }}
        >
          {label}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>{record.status}</span>

        {!isApproved && (
          <IconButton size="small" onClick={() => setOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* ✅ UPDATED: PASS AMOUNT VALUES */}
      <PledgeStatusDialog
        open={open}
        onClose={() => setOpen(false)}
        currentStatus={record.status}
        resourceId={record._id}
        apiPath="updatepledges"
        options={getOptions()}
        paymentMade={record.paymentMade}
        remainingAmount={record.remainingAmount}
      />
    </div>
  );
};

/* =========================
   CUSTOM DATE FIELD
========================= */
const DateTimeField = ({ source }) => {
  const record = useRecordContext();
  if (!record || !record[source]) return null;

  const date = new Date(record[source]);

  return (
    <span>
      {date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}{" "}
      {date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}
    </span>
  );
};

/* =========================
   LIST PLEDGES
========================= */
const ListPledges = (props) => (
  <List
    {...props}
    title="New Pledge Request"
    bulkActionButtons={false}
    filters={<PledgeFilters />}
  >
    <Datagrid rowClick={false}>
      <TextField source="productName" label="Product Name" />
      <NumberField source="quantity" />
      <NumberField source="installment" label="Installment No." />
      <NumberField source="paymentMade" label="Paid Amount" />
      <NumberField source="remainingAmount" label="Remaining Amount" />
      <TextField source="adminReason" label="Admin Reason" />
      <TextField source="userEmail" label="Donor Email" />
      <TextField source="userName" label="Donor Name" />
      <TextField source="userPhone" label="Donor Phone" />
      <StatusWithEditIcon label="Status" />
      <DateTimeField source="createdAt" label="Created At" />

      {/* DELETE */}
      <DeletePledgeButton />
    </Datagrid>
  </List>
);

export default ListPledges;